import { useEffect, useState } from "react";
import { Volume2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * VoicePicker — lets the learner choose their TTS voice. Tapping a card
 * plays a short preview in that voice (cached server-side). "Use this voice"
 * saves the choice on the user document so every future TTS call uses it.
 */
export default function VoicePicker() {
    const { refresh } = useAuth();
    const [voices, setVoices] = useState([]);
    const [current, setCurrent] = useState(null);
    const [playing, setPlaying] = useState(null);
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        api.get("/settings/voices").then((r) => setVoices(r.data)).catch(() => {});
        api.get("/auth/me").then((r) => setCurrent(r.data?.voice || "sage")).catch(() => {});
    }, []);

    const preview = async (voice) => {
        if (playing) return;
        setPlaying(voice);
        try {
            const { data } = await api.post("/tts", {
                text: "Reading is a right, not a privilege.",
                voice,
                mode: "text",
            });
            await new Audio(`data:${data.mime};base64,${data.audio_base64}`).play().catch(() => {});
        } catch {
            toast.error("Preview didn't play. Try again in a moment.");
        } finally {
            setPlaying(null);
        }
    };

    const choose = async (voice) => {
        if (saving) return;
        setSaving(voice);
        try {
            await api.put("/settings/voice", { voice });
            setCurrent(voice);
            // Refresh AuthContext so the nav badge updates immediately.
            refresh?.();
            toast.success("Voice updated");
        } catch {
            toast.error("Could not save your voice choice.");
        } finally {
            setSaving(null);
        }
    };

    if (!voices.length) return null;

    return (
        <section
            className="card-rw p-6 md:p-7 mb-12"
            style={{ borderLeft: "6px solid #1F4237" }}
            data-testid="voice-picker"
        >
            <p className="label-eyebrow mb-1">Voice · pick your reader</p>
            <h3
                className="text-2xl font-medium text-[#1C2321] mb-2"
                style={{ fontFamily: "Outfit" }}
            >
                Choose the voice you learn best with.
            </h3>
            <p className="text-[#515B57] leading-relaxed mb-5">
                Every word, phoneme, and lesson is read to you in this voice. Tap Preview to hear
                a sample, then Use this voice to save.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {voices.map((v) => {
                    const isCurrent = current === v.key;
                    return (
                        <div
                            key={v.key}
                            data-testid={`voice-card-${v.key}`}
                            className="rounded-xl border border-[#E5E7E2] bg-white p-4 flex flex-col"
                            style={isCurrent ? { borderColor: "#2D5D4E", background: "#F4F8F4" } : undefined}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h4
                                    className="text-lg font-medium text-[#1C2321]"
                                    style={{ fontFamily: "Outfit" }}
                                >
                                    {v.label}
                                </h4>
                                {isCurrent && (
                                    <span
                                        className="inline-flex items-center gap-1 text-xs font-medium text-[#2D5D4E]"
                                        data-testid={`voice-current-${v.key}`}
                                    >
                                        <Check className="w-3.5 h-3.5" strokeWidth={2} /> Current
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-[#515B57] mb-4 min-h-[1.25rem]">{v.vibe}</p>
                            <div className="mt-auto flex gap-2">
                                <button
                                    onClick={() => preview(v.key)}
                                    disabled={!!playing}
                                    data-testid={`voice-preview-${v.key}`}
                                    className="flex-1 inline-flex items-center justify-center gap-2 py-2 rounded-lg border border-[#E5E7E2] bg-white text-[#2D5D4E] text-sm font-medium hover:bg-[#E8E2D2] disabled:opacity-60"
                                >
                                    {playing === v.key ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />
                                    ) : (
                                        <Volume2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                                    )}
                                    Preview
                                </button>
                                <button
                                    onClick={() => choose(v.key)}
                                    disabled={isCurrent || !!saving}
                                    data-testid={`voice-use-${v.key}`}
                                    className="flex-1 inline-flex items-center justify-center py-2 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-sm font-medium hover:bg-[#1F4237] disabled:opacity-40 disabled:cursor-default"
                                >
                                    {saving === v.key
                                        ? "Saving…"
                                        : isCurrent
                                            ? "In use"
                                            : "Use this voice"}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
